import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { DashboardCustomizeBar } from '../../components/DashboardCustomizeBar';
import { DashboardLayoutEditor } from '../../components/DashboardLayoutEditor';
import { DashboardLayoutRenderer } from '../../components/DashboardLayoutRenderer';
import { DashboardWidgetProps } from '../../config/dashboardWidgets/types';
import { layoutNeedsWarrantsToggle } from '../../config/dashboardWidgets/layoutRows';
import { styles } from './HomeScreen.styles';
import { HomeScreenActions, HomeScreenProps, HomeScreenState } from './useHomeScreen';

interface HomeDashboardSectionProps {
  readonly screenProps: HomeScreenProps;
  readonly state: HomeScreenState;
  readonly actions: HomeScreenActions;
  readonly forceSingleColumn: boolean;
}

export const HomeDashboardSection: React.FC<HomeDashboardSectionProps> = ({
  screenProps,
  state,
  actions,
  forceSingleColumn,
}) => {
  const {
    sidebarOpen = false,
    sidebarCollapsed = false,
    ownershipScope = 'group',
    onProductPress,
  } = screenProps;
  const {
    issuerDataProducts,
    issuerDataNotional,
    loading,
    error,
    includeWarrants,
    exposureItems,
    exposureLoading,
    notionalDisplayCurrency,
    layout,
    layoutReady,
    isEditingLayout,
    isSavingLayout,
    isLayoutDirty,
    canResetLayout,
    layoutSaveError,
  } = state;
  const {
    setIncludeWarrants,
    handleCurrencyPress,
    handleExpiryPress,
    handleExposurePress,
    handleIssuerPress,
    handleCalendarDayPress,
    startLayoutEditing,
    cancelLayoutEditing,
    resetLayoutToDefault,
    saveLayout,
    addDraftWidget,
    removeDraftWidget,
    moveDraftWidget,
    swapDraftWidgets,
    setDraftWidgetWidthSpan,
    setDraftColumnCount,
  } = actions;

  if (!layoutReady) {
    return null;
  }

  const widgetProps: DashboardWidgetProps = {
    ownershipScope,
    includeWarrants,
    hideLegendDetails: sidebarOpen && !sidebarCollapsed,
    onProductPress: isEditingLayout ? undefined : onProductPress,
    onCurrencyPress: isEditingLayout ? undefined : handleCurrencyPress,
    onExpiryPress: isEditingLayout ? undefined : handleExpiryPress,
    onExposurePress: isEditingLayout ? undefined : handleExposurePress,
    onIssuerPress: isEditingLayout ? undefined : handleIssuerPress,
    onCalendarDayPress: isEditingLayout ? undefined : handleCalendarDayPress,
    notifications: screenProps.notifications ?? [],
    onNotificationPress: isEditingLayout ? undefined : screenProps.onNotificationPress,
    onGoToNotifications: isEditingLayout ? undefined : screenProps.onGoToNotifications,
    onGoToEvents: isEditingLayout ? undefined : screenProps.onNavigateToEvents,
    issuerDataNotional,
    issuerDataProducts,
    issuerLoading: loading,
    issuerError: error,
    notionalDisplayCurrency,
    exposureItems,
    exposureLoading,
  };

  return (
    <>
      <DashboardCustomizeBar
        isEditing={isEditingLayout}
        isSaving={isSavingLayout}
        isDirty={isLayoutDirty}
        canReset={canResetLayout}
        saveError={layoutSaveError}
        onStartEditing={startLayoutEditing}
        onCancel={cancelLayoutEditing}
        onReset={resetLayoutToDefault}
        onSave={() => {
          void saveLayout();
        }}
      />

      {isEditingLayout ? (
        <DashboardLayoutEditor
          layout={layout}
          isMobileViewport={forceSingleColumn}
          onAddWidget={addDraftWidget}
          onRemoveWidget={removeDraftWidget}
          onMoveWidget={(widgetId, direction) =>
            moveDraftWidget(widgetId, direction, { forceSingleColumn })
          }
          onSetWidthSpan={setDraftWidgetWidthSpan}
          onSetColumnCount={setDraftColumnCount}
        />
      ) : null}

      {layoutNeedsWarrantsToggle(layout) && (
        <View style={forceSingleColumn ? undefined : styles.checkboxRowDesktop}>
          <TouchableOpacity
            style={styles.checkboxRow}
            onPress={() => setIncludeWarrants(!includeWarrants)}
            activeOpacity={0.7}
          >
            <View style={[styles.checkbox, includeWarrants && styles.checkboxChecked]}>
              {includeWarrants ? <Text style={styles.checkmark}>✓</Text> : null}
            </View>
            <Text style={styles.checkboxLabel}>Include warrants</Text>
          </TouchableOpacity>
        </View>
      )}

      <DashboardLayoutRenderer
        layout={layout}
        widgetProps={widgetProps}
        forceSingleColumn={forceSingleColumn}
        isPreview={isEditingLayout}
        onSwapWidgets={isEditingLayout ? swapDraftWidgets : undefined}
        onSetWidthSpan={isEditingLayout ? setDraftWidgetWidthSpan : undefined}
      />
    </>
  );
};
